import {isEmpty, map, sortBy} from 'lodash';
import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {userApi} from 'api';
import {aboutApi} from 'api/about';
import {Button, Card, ConfirmModal, ConnectionInfo, DataWrapper, Divider, PageHeader, PredefinedNotifierType} from 'components';
import {UserContext} from 'context';
import {useApiData} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {UserConnectionInfo} from 'types';
import {apiUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.USER.ABOUT.ME;

export const AboutMePage: React.FC = () => {
    const {t} = useTranslation();

    const {socket} = useContext(UserContext);
    const {send: loadAboutMe, data, loading} = useApiData(aboutApi.me, {loadingDefault: true});

    const [confirmDisconnectModalProps, setConfirmDisconnectModalProps] =
        useState<{open: boolean; session?: UserConnectionInfo}>({open: false});

    const handleDisconnectSuccess = useCallback(() => {
        setConfirmDisconnectModalProps({open: false});
        loadAboutMe();
    }, [loadAboutMe]);

    const buildNotifierErrorProps = useCallback((error) => {
        if (apiUtils.isResourceNotFoundError(error)) {
            return {title: t(T_KEYS.LABELS.USER_NOT_ONLINE)};
        }
    }, [t]);

    const {send: disconnect} = useApiData(userApi.disconnectActiveSession, {
        onSuccess: handleDisconnectSuccess,
        notificationType: PredefinedNotifierType.Send,
        buildNotifierErrorProps
    });

    // active first then newest on top
    const activeSessions = useMemo(() => sortBy(data?.activeSessions, (session) =>
        session.socketId === socket.id ? -new Date() : -new Date(session.date)), [data?.activeSessions, socket?.id]
    );

    const handleDisconnectClick = useCallback((session: UserConnectionInfo) => {
        setConfirmDisconnectModalProps({open: true, session});
    }, []);

    const onConfirmDisconnectModalClose = useCallback((confirm: boolean) => {
        if (confirm) {
            disconnect(confirmDisconnectModalProps.session?.socketId);
        } else {
            setConfirmDisconnectModalProps({open: false});
        }
    }, [confirmDisconnectModalProps.session?.socketId, disconnect]);

    useEffect(() => {
        loadAboutMe();
    }, [loadAboutMe]);

    return (
        <Fragment>
            <PageHeader
                title={t(T_KEYS.TITLE)}
                description={t(T_KEYS.DESCRIPTION)}
                hideableTitle={false}
            />
            <DataWrapper
                isLoading={loading}
                svgClassName='tw-max-h-8'
            >
                <div className='tw-max-w-92 tw-grid tw-gap-5'>
                    <Card>
                        <div className='tw-ts-h4-bold tw-mb-3'>{t(T_KEYS.FIELDS.COMMON)}</div>
                        <div className='tw-mb-1'>
                            <span className='tw-font-bold tw-mr-2'>{`${t(T_KEYS.FIELDS.IP)}:`}</span>
                            {data?.ip}
                        </div>
                        <div>
                            <span className='tw-font-bold tw-mr-2'>{`${t(T_KEYS.FIELDS.TIMEZONE)}:`}</span>
                            {data?.timeZone}
                        </div>
                    </Card>
                    {!isEmpty(activeSessions) &&
                        <Card>
                            <div className='tw-ts-h4-bold tw-mb-3'>{t(T_KEYS.FIELDS.ACTIVE_SESSIONS)}</div>
                            {map(activeSessions, (session, index) => (
                                <div key={index} className='tw-mb-3'>
                                    <ConnectionInfo info={session} />
                                    <div>
                                        {session.socketId === socket.id && activeSessions?.length > 1 && <Divider className='tw-my-3' />}
                                        {session.socketId !== socket.id &&
                                            <Button
                                                color='attention'
                                                width='small'
                                                height='auto'
                                                className='tw-mt-2'
                                                onClick={() => handleDisconnectClick(session)}
                                            >
                                                {t(T_KEYS.LABELS.DISCONNECT)}
                                            </Button>}
                                    </div>
                                </div>
                            ))}
                        </Card>}
                    {!isEmpty(data?.lastLogins) &&
                        <Card>
                            <div className='tw-ts-h4-bold tw-mb-3'>{t(T_KEYS.FIELDS.LAST_LOGINS)}</div>
                            {map(data.lastLogins, (login, index) => (
                                <div key={index} className='tw-mb-3'>
                                    <ConnectionInfo info={login} />
                                </div>
                            ))}
                        </Card>}
                </div>
            </DataWrapper>
            <ConfirmModal
                title={t(T_KEYS.LABELS.DISCONNECT)}
                maxWidth='xs'
                fullWidth
                onClose={onConfirmDisconnectModalClose}
                {...confirmDisconnectModalProps}
            >
                <div className='tw-mb-6'>{t(T_KEYS.LABELS.DISCONNECT_CONFIRM)}</div>
                {confirmDisconnectModalProps.session && <ConnectionInfo info={confirmDisconnectModalProps.session} />}
            </ConfirmModal>
        </Fragment>
    );
};